import { useTranslation } from '@mypoz/i18n';
import { Card, Icon, StatCard } from '@mypoz/ui';

import type { WalletState } from '@/features/wallet/context.js';
import { useFormat } from '@/lib/format.js';

export interface WalletSummaryCardProps {
  wallet: Pick<WalletState, 'balanceCents' | 'currency' | 'onHoldCents'>;
  /** Completed top-ups (MYP-107). Undefined while the history loads or if it failed: shown as a dash. */
  topUpCount?: number;
}

/**
 * The Wallet page's one card (MYP-54): balance up top, on hold / top-ups as a
 * two-column stat row underneath. The auto-pay toggle that used to close it (MYP-56) was removed:
 * auto-pay is now chosen per order, on Step 3 of Create order. MYP-55 and MYP-57 each add their own piece elsewhere on the page, so this component stays
 * just the summary, not the whole screen.
 *
 * The balance header mirrors `WalletBalanceCard` (the Dashboard's teaser) rather than importing
 * it — that component always renders a "Top up" link to somewhere else, which doesn't make sense
 * on the page top-up already leads to, and Dashboard-specific components stay feature-local by the
 * same convention `StatTile`'s own doc comment explains.
 *
 * `StatCard` (from `@mypoz/ui`) is the right fit for the two sub-stats: a plain icon-and-value
 * row with no loading/error state or call to action, which is all these fixture numbers need.
 * Overridden with flat, unshadowed styling so they read as this card's own contents rather
 * than separate elevated cards nested inside it.
 */
export function WalletSummaryCard({ wallet, topUpCount }: WalletSummaryCardProps) {
  const { t } = useTranslation();
  const fmt = useFormat();

  return (
    <Card className="relative overflow-hidden p-6 sm:p-7">
      <span className="absolute inset-x-0 top-0 h-1 bg-brand" />

      <div className="relative">
        <div className="mb-4 flex items-center gap-3">
          <span className="flex size-11 shrink-0 items-center justify-center rounded-md bg-brand-soft text-brand">
            <Icon name="account_balance_wallet" size={23} fill />
          </span>
          <p className="text-sm font-semibold tracking-[0.08em] text-muted uppercase">
            {t('wallet.balance.label')}
          </p>
        </div>

        <p className="text-4xl leading-none font-bold tracking-tight text-ink tabular-nums sm:text-5xl">
          {fmt.money(wallet.balanceCents, wallet.currency)}
        </p>
        <p className="mt-2 text-sm text-muted">{t('wallet.balance.spendable')}</p>

        <div className="mt-6 grid grid-cols-1 gap-3 border-t border-line pt-6 sm:grid-cols-2">
          <StatCard
            label={t('wallet.balance.onHold')}
            value={fmt.money(wallet.onHoldCents, wallet.currency)}
            className="bg-surface-alt p-4 shadow-none"
          />
          <StatCard
            label={t('wallet.balance.topUps')}
            value={topUpCount === undefined ? '—' : fmt.number(topUpCount)}
            className="bg-surface-alt p-4 shadow-none"
          />
        </div>
      </div>
    </Card>
  );
}
